import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { NAV_GROUPS } from '../../constants/nav';
import type { Screen } from '../../types';

interface Props {
  forceCollapsed?: boolean;
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

// Which groups are expanded by default
const DEFAULT_OPEN: Record<string, boolean> = { library: true, demohub: false };

export default function Sidebar({ forceCollapsed, isMobile, isOpen, onClose }: Props) {
  const { screen, go, currentUser, logout, nudges, leads, playbooks, setLibraryTab } = useApp();
  const [userCollapsed, setUserCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(DEFAULT_OPEN);

  const isCollapsed = isMobile ? false : (userCollapsed || !!forceCollapsed);

  const toggleGroup = (id: string) => setOpenGroups(p => ({ ...p, [id]: !p[id] }));

  // Dynamic badge counts
  const hotLeads = leads.filter(l => l.heat === 'hot' && l.status === 'new').length;
  const pendingNudges = nudges.filter(n => !n.sent).length;
  const playbookCount = playbooks.length;

  function getBadge(id: string): { text?: string; red?: boolean; green?: boolean } | null {
    if (id === 'leads')    return hotLeads > 0 ? { text: `${hotLeads} live`, green: true } : null;
    if (id === 'nudges')   return pendingNudges > 0 ? { text: String(pendingNudges) } : null;
    if (id === 'playbooks') return { text: String(playbookCount) };
    if (id === 'tracking') return { text: '2' };
    if (id === 'home')     return null;
    return null;
  }

  function isActive(itemId: string, itemScreen?: string): boolean {
    if (itemId === 'collaterals' && screen === 'admin') return true;
    if (itemId === 'playbooks'   && screen === 'admin') return false; // handled by tab
    if (itemScreen) return screen === itemScreen;
    return false;
  }

  function navigate(itemScreen?: Screen, tab?: 'collaterals' | 'playbooks') {
    if (!itemScreen) return;
    if (tab) setLibraryTab(tab);
    go(itemScreen);
    onClose?.();
  }

  // Shared nav item style
  const itemStyle = (active: boolean, depth = 0): React.CSSProperties => ({
    display: 'flex', alignItems: 'center',
    gap: isCollapsed ? 0 : '10px',
    padding: isCollapsed ? '10px 0' : depth === 0 ? '8px 16px' : '7px 16px 7px 38px',
    cursor: 'pointer', transition: 'background .12s',
    position: 'relative',
    color: active ? '#E8B55A' : depth > 0 ? 'rgba(255,255,255,.55)' : 'rgba(255,255,255,.7)',
    fontSize: depth > 0 ? '11px' : '12px',
    fontWeight: depth > 0 ? '400' : '500',
    border: 'none',
    background: active ? 'rgba(200,151,58,.15)' : 'none',
    fontFamily: 'inherit', width: '100%',
    textAlign: isCollapsed ? 'center' : 'left',
    justifyContent: isCollapsed ? 'center' : undefined,
    whiteSpace: 'nowrap',
  });

  return (
    <>
      {isMobile && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 299,
            background: 'rgba(0,0,0,.55)',
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? 'auto' : 'none',
            transition: 'opacity .25s',
          }}
        />
      )}
    <div style={{
      width: isMobile ? '280px' : (isCollapsed ? '54px' : '224px'),
      background: '#0D1825',
      flexShrink: 0, display: 'flex', flexDirection: 'column',
      overflow: 'hidden', height: '100vh',
      transition: isMobile ? 'transform .25s' : 'width .22s',
      ...(isMobile ? {
        position: 'fixed' as const,
        left: 0, top: 0, bottom: 0,
        zIndex: 300,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
        boxShadow: isOpen ? '4px 0 24px rgba(0,0,0,.5)' : 'none',
      } : {}),
    }}>

      {/* Brand row */}
      <div style={{
        padding: isCollapsed ? '14px 0' : '14px 14px 12px',
        display: 'flex', alignItems: 'center', gap: '10px',
        borderBottom: '1px solid rgba(255,255,255,.07)',
        flexShrink: 0, justifyContent: isCollapsed ? 'center' : undefined,
      }}>
        <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: '#C8973A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800', color: '#0F1B2D', flexShrink: 0 }}>S</div>
        {!isCollapsed && (
          <>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'white' }}>SalesBook</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.4)', marginTop: '1px' }}>{currentUser?.title}</div>
            </div>
            {isMobile ? (
              <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.4)', cursor: 'pointer', fontSize: '18px', flexShrink: 0, padding: '2px', lineHeight: 1 }}>
                <i className="ti ti-x" />
              </button>
            ) : !forceCollapsed && (
              <button onClick={() => setUserCollapsed(true)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '15px', flexShrink: 0, padding: '2px', lineHeight: 1 }}>
                <i className="ti ti-layout-sidebar-left-collapse" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Live notification banner */}
      {!isCollapsed && (
        <div style={{ margin: '8px 10px 0', padding: '8px 10px', background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.25)', borderRadius: '8px', flexShrink: 0, display: 'flex', alignItems: 'flex-start', gap: '7px' }}>
          <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#22C55E', flexShrink: 0, marginTop: '3px', boxShadow: '0 0 0 3px rgba(34,197,94,.25)' }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#22C55E', marginBottom: '1px' }}>Priya Sharma</div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,.5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>on page 4 — Pricing</div>
          </div>
          <button onClick={() => go('tracking')} style={{ fontSize: '9px', fontWeight: '700', color: '#E8B55A', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0, padding: 0, whiteSpace: 'nowrap' }}>View live →</button>
        </div>
      )}

      {/* Expand button when user-collapsed */}
      {isCollapsed && !forceCollapsed && (
        <button onClick={() => setUserCollapsed(false)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.3)', cursor: 'pointer', fontSize: '15px', padding: '10px 0', display: 'flex', justifyContent: 'center', flexShrink: 0 }}
          onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,.7)')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,.3)')}>
          <i className="ti ti-layout-sidebar-left-expand" />
        </button>
      )}

      {/* Nav scroll area */}
      <nav style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: '8px' }}>
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi}>
            {/* Section header */}
            {group.group && !isCollapsed && (
              <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '.1em', color: 'rgba(255,255,255,.3)', padding: '14px 16px 4px', whiteSpace: 'nowrap' }}>
                {group.group}
              </div>
            )}
            {group.group && isCollapsed && (
              <div style={{ height: '1px', background: 'rgba(255,255,255,.06)', margin: '6px 10px' }} />
            )}

            {group.items.map(item => {
              const dynamicBadge = getBadge(item.id);
              const badge = dynamicBadge || (item.badge ? { text: item.badge, red: item.badgeRed, green: item.badgeGreen } : null);
              const hasChildren = !!item.children?.length;
              const isOpen = openGroups[item.id] ?? false;
              const parentActive = hasChildren
                ? item.children!.some(c => c.screen === screen)
                : screen === item.screen;

              return (
                <div key={item.id}>
                  {/* Parent item */}
                  <button
                    style={itemStyle(parentActive && !hasChildren, 0)}
                    title={isCollapsed ? item.label : undefined}
                    onClick={() => {
                      if (hasChildren) { if (!isCollapsed) toggleGroup(item.id); }
                      else navigate(item.screen as Screen | undefined);
                    }}
                    onMouseEnter={e => { if (!parentActive) e.currentTarget.style.background = 'rgba(255,255,255,.05)'; }}
                    onMouseLeave={e => { if (!parentActive) e.currentTarget.style.background = 'none'; }}
                  >
                    {(parentActive && !hasChildren) && (
                      <span style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: '3px', height: '20px', background: '#C8973A', borderRadius: '0 3px 3px 0' }} />
                    )}
                    <i className={`ti ${item.icon}`} style={{ fontSize: '16px', flexShrink: 0, width: '18px', textAlign: 'center' }} />
                    {!isCollapsed && (
                      <>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>{item.label}</span>
                        {badge && (
                          <span style={{
                            fontSize: '9px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px', flexShrink: 0,
                            background: badge.green ? 'rgba(34,197,94,.2)' : badge.red ? '#EF4444' : 'rgba(200,151,58,.25)',
                            color:      badge.green ? '#22C55E'            : badge.red ? 'white'    : '#E8B55A',
                            border:     badge.green ? '1px solid rgba(34,197,94,.4)' : 'none',
                          }}>{badge.text}</span>
                        )}
                        {hasChildren && (
                          <i className={`ti ti-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize: '12px', flexShrink: 0, color: 'rgba(255,255,255,.3)' }} />
                        )}
                      </>
                    )}
                  </button>

                  {/* Sub-items */}
                  {hasChildren && isOpen && !isCollapsed && (
                    <div style={{ overflow: 'hidden' }}>
                      {item.children!.map(child => {
                        const childActive = screen === child.screen;
                        const childBadge = getBadge(child.id) || (child.badge ? { text: child.badge } : null);
                        return (
                          <button key={child.id}
                            style={itemStyle(childActive, 1)}
                            onClick={() => navigate(child.screen, child.tab)}
                            onMouseEnter={e => { if (!childActive) e.currentTarget.style.background = 'rgba(255,255,255,.04)'; }}
                            onMouseLeave={e => { if (!childActive) e.currentTarget.style.background = 'none'; }}
                          >
                            {childActive && (
                              <span style={{ position: 'absolute', left: '22px', top: '50%', transform: 'translateY(-50%)', width: '3px', height: '16px', background: '#C8973A', borderRadius: '0 3px 3px 0' }} />
                            )}
                            <i className={`ti ${child.icon}`} style={{ fontSize: '13px', flexShrink: 0, width: '14px', textAlign: 'center' }} />
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left' }}>{child.label}</span>
                            {childBadge && (
                              <span style={{ fontSize: '9px', fontWeight: '700', borderRadius: '20px', padding: '1px 7px', flexShrink: 0, background: 'rgba(200,151,58,.2)', color: '#E8B55A' }}>{childBadge.text}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User footer */}
      <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(255,255,255,.07)', flexShrink: 0 }}>
        <div onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '6px 8px', borderRadius: '8px', justifyContent: isCollapsed ? 'center' : undefined, transition: 'background .12s' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,.06)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
          <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: currentUser?.color || '#C8973A', color: '#0F1B2D', fontSize: '10px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {currentUser?.ini}
          </div>
          {!isCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '11px', fontWeight: '600', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentUser?.name}</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,.35)' }}>{currentUser?.role}</div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
